
export const weightFacts = {
  kilogram: {
    name: "Kilogram",
    shortDescription: "The base unit of mass in the International System of Units (SI)",
    history: "Originally defined in 1795 as the mass of one cubic decimeter of water at its melting point, it was later standardized by the International Prototype Kilogram (IPK) - a platinum-iridium cylinder kept in Paris. For 130 years, this single object defined mass for the entire world, making it the last physical artifact used to define any SI unit.",
    origin: "Created during the French Revolution as part of the metric system's sweeping reforms of measurement",
    modernUsage: "The IPK's mysterious weight fluctuations led to its 2019 replacement by a definition based on Planck's constant, marking a historic shift from physical artifacts to universal constants in measurement standards.",
    funFacts: [
      "The original prototype kilogram mysteriously lost about 50 micrograms over a century, prompting intense scientific debate about the nature of mass itself",
      "The new definition of the kilogram involves measuring the precise energy needed to counteract the weight of a mass using electromagnetic forces",
      "Before standardization, France alone had over 250,000 different units of weight, varying by region and trade"
    ],
    related: ["gram", "milligram", "pound"],
    images: []
  },
  milligram: {
    name: "Milligram",
    shortDescription: "One-thousandth of a gram, crucial for precise measurements",
    history: "The milligram, one-thousandth of a gram, exemplifies the metric system's ability to scale for specialized needs. Its adoption revolutionized pharmacy and chemistry, where precise measurements of small quantities became possible.",
    origin: "Derived from the gram as part of the metric system's development",
    modernUsage: "The unit became particularly important in the standardization of medication dosages, replacing older apothecary units like grains and scruples that lacked decimal convenience.",
    funFacts: [
      "Development of analytical balances in 19th century enabled precise measurements",
      "Replaced older apothecary units like grains and scruples",
      "Critical in modern pharmaceutical dosing"
    ],
    related: ["gram", "kilogram", "grain"],
    images: []
  },
  pound: {
    name: "Pound",
    shortDescription: "A unit of mass equal to 16 ounces or approximately 0.45 kilograms",
    history: "The pound evolved from the Roman libra (hence the abbreviation 'lb'). Different pounds developed for different trades - the tower pound for precious metals, the merchant's pound for general trade, and the london pound for wool.",
    origin: "From the Roman 'libra' (hence 'lb')",
    modernUsage: "The avoirdupois pound (16 ounces) became standard, though the troy pound persisted for precious metals.",
    funFacts: [
      "Different types existed for different trades",
      "Medieval markets kept standard weights with city arms",
      "Regular inspections ensured accuracy"
    ],
    related: ["ounce", "kilogram", "stone"],
    images: []
  },
  ounce: {
    name: "Ounce",
    shortDescription: "A unit of mass equal to 1/16 of a pound",
    history: "The ounce derives from the Roman uncia, meaning 'twelfth part' (of a libra). The avoirdupois ounce (1/16 of a pound) competed with the troy ounce (1/12 of a troy pound), leading to the modern distinction between precious metal weight (troy) and general merchandise (avoirdupois).",
    origin: "From Latin 'uncia' meaning 'twelfth part'",
    modernUsage: "Different systems persist: avoirdupois for general use, troy for precious metals",
    funFacts: [
      "Apothecaries used special marked weights to prevent mixing errors",
      "Troy ounces still used for precious metals",
      "Name shares origin with the word 'inch'"
    ],
    related: ["pound", "grain", "dram"],
    images: []
  },
  gram: {
    name: "Gram",
    shortDescription: "The basic unit of mass in the metric system",
    history: "The gram, defined as the mass of one cubic centimeter of water at its maximum density, embodied the metric system's attempt to derive units from natural constants. Originally intended as the base unit of mass, it proved too light for practical use, leading to the kilogram's adoption instead.",
    origin: "Defined based on the mass of water",
    modernUsage: "Widely used for measuring small masses in cooking, science, and commerce",
    funFacts: [
      "Originally intended as the base unit of mass",
      "Equal to the mass of one cubic centimeter of water",
      "Early devices included precision balance scales"
    ],
    related: ["kilogram", "milligram", "ounce"],
    images: []
  },
  ton: {
    name: "Ton",
    shortDescription: "A large unit of mass with several variations",
    history: "The ton evolved from the 'tun,' a large wine cask. The weight ton developed as the weight of water displaced by one tun (approximately 2000 pounds).",
    origin: "From 'tun', a large wine cask",
    modernUsage: "Different tons are used: short ton (2000 lbs), long ton (2240 lbs), and metric tonne (1000 kg)",
    funFacts: [
      "Name comes from wine cask measurement",
      "Three main variations exist worldwide",
      "Weighing devices evolved from balance scales to mechanical platforms"
    ],
    related: ["pound", "kilogram"],
    images: []
  },
  carat: {
    name: "Carat",
    shortDescription: "A unit of mass used for precious stones, equal to 200 milligrams",
    history: "The carat, originally based on the uniform weight of carob seeds, became standardized for weighing precious stones. The modern carat (200 milligrams) was adopted internationally in 1907, though some regions resisted change - the British Board of Trade officially accepted it only in 1914.",
    origin: "Based on the weight of carob seeds",
    modernUsage: "Used globally for measuring gemstones and pearls with the standardized value of 200 milligrams",
    funFacts: [
      "Different regions had varying carat standards before 1907",
      "The British resisted adopting the metric carat until 1914",
      "Jewelers used specially decorated carat weights and scales"
    ],
    related: ["grain", "milligram", "gram"],
    images: []
  },
  grain: {
    name: "Grain",
    shortDescription: "The smallest traditional weight unit, based on a grain of wheat",
    history: "The grain, the smallest traditional weight unit, was based on the remarkably consistent weight of a grain of wheat or barley. This natural standardization made it ideal for measuring precious materials and medicines. The grain remains in use today for measuring arrows, gunpowder, and pharmaceutical products, making it one of the oldest units still in practical use.",
    origin: "Based on the weight of a single grain of wheat or barley",
    modernUsage: "Still used for measuring arrows, gunpowder, and some pharmaceutical products",
    funFacts: [
      "Exactly equals 64.79891 milligrams in modern standards",
      "One of the oldest units still in practical use",
      "Historical grain weights were kept in nested brass sets"
    ],
    related: ["carat", "milligram", "dram"],
    images: []
  },
  shekel: {
    name: "Shekel",
    shortDescription: "An ancient unit of weight that originated in Mesopotamia around 3000 BCE",
    history: "The shekel, one of the oldest weight units, originated in Mesopotamia around 3000 BCE. Initially representing 180 barley grains, it became standardized in various forms throughout the ancient Near East. The shekel gained additional significance as one of the earliest currency units, with silver shekels serving as both weight and monetary standards.",
    origin: "Originated in ancient Mesopotamia, based on barley grains",
    modernUsage: "Primarily of historical interest, though the name lives on as Israel's modern currency",
    funFacts: [
      "Originally based on 180 barley grains",
      "Served as both weight and currency unit",
      "Archaeological finds include officially marked shekel weights"
    ],
    related: ["grain", "talent", "dram"],
    images: []
  },
  talent: {
    name: "Talent",
    shortDescription: "One of the largest ancient weight units, equal to about 75 pounds",
    history: "The talent, one of the largest ancient weight units, originated in Mesopotamia and became crucial in the ancient Mediterranean world. One talent equaled about 60 minas or 3,000 shekels (approximately 75 pounds or 34 kilograms). The unit gained particular significance as a measure of precious metals and in biblical contexts, where the 'talent of gold' became synonymous with great wealth.",
    origin: "Ancient Mesopotamian unit adopted throughout the Mediterranean",
    modernUsage: "The word's modern meaning - natural aptitude or skill - derives from the Parable of the Talents in the New Testament, where talents represented valuable coins",
    funFacts: [
      "Equal to about 75 pounds or 34 kilograms",
      "Mentioned prominently in biblical texts",
      "Modern meaning of 'talent' comes from biblical parable"
    ],
    related: ["shekel", "pound", "stone"],
    images: []
  },
  dram: {
    name: "Dram",
    shortDescription: "A weight unit originating in ancient Greece, equal to 1/16 of an ounce",
    history: "The dram (or drachm) originated in ancient Greece as both a coin and a weight measure. The avoirdupois dram (1/16 of an ounce) became standardized in England, while apothecaries used a slightly different dram for medicines. Remarkably, the dram survived into the modern era as a measure for Scotch whisky in British pubs until the 1970s.",
    origin: "Ancient Greek unit used as both weight and currency",
    modernUsage: "Was used for measuring Scotch whisky in British pubs until the 1970s",
    funFacts: [
      "Different versions existed for trade and medicine",
      "Used for measuring Scotch whisky until 1970s",
      "Apothecary weights had distinctive chevron shapes"
    ],
    related: ["ounce", "grain", "stone"],
    images: []
  },
  stone: {
    name: "Stone",
    shortDescription: "A British weight unit equal to 14 pounds",
    history: "The stone weight emerged in medieval trade, varying by commodity and region - wool stone differed from wool stone, which differed from lead stone. This variation highlights how medieval commerce adapted measurements to specific trades. The modern stone (14 pounds) standardized in Britain during the 19th century.",
    origin: "Medieval trading unit that varied by commodity",
    modernUsage: "Still used in Britain and Ireland for measuring human body weight",
    funFacts: [
      "Different stone weights existed for different goods",
      "Standardized to 14 pounds in the 19th century",
      "Remains in use for body weight in UK and Ireland"
    ],
    related: ["pound", "talent", "dram"],
    images: []
  }
};
